/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react'
import {useDispatch, useSelector} from 'react-redux'
import { get, map } from 'lodash'
import { useHistory, useParams } from 'react-router-dom'
import { Row, Col } from 'react-bootstrap'
import { jsx, css } from '@emotion/react'
import { Trans, Translation } from 'react-i18next'
import { Timestamp } from './Timestamp'
import { molliePaymentList } from '../actions/mollie_payment'
import { customerList } from '../actions/customer'
import LoadingOverlay from './layout/loading_overlay/LoadingOverlay'
import { Modal } from 'react-bootstrap'
import { BankTransferPaymentDetails } from './BankTransferPaymentDetails'
import ToolTip from './ToolTip'
import WrappingBusyMask from './WrappingBusyMask'
import { Button } from 'react-bootstrap'
import { BlueButton } from './layout/BlueButton'
import { GrayButton } from './layout/GrayButton'
import { RedButton } from './layout/RedButton'
import { handleSubmitResult } from '../actions/form'
import { Separator } from './layout/Separator'
import CurrencyValue from './CurrencyValue'
import { ButtonBar } from './layout/ButtonBar'
import { topupList } from '../actions/topup'

export const PayTopupMethodChoicePopup = ({topup, onClose}) => {

    const dispatch = useDispatch()
    const history = useHistory()
    const [payByMollieLoading, setPayByMollieLoading] = useState(false)
    const [payByBankTransfer, setPayByBankTransfer] = useState(false)
    const [navigatingToMollie, setNavigatingToMollie] = useState(false)
    const [inPayByBankTransfer, setInPayByBankTransfer] = useState(false)
    
    dispatch(customerList.ensureCustomerLoaded())
    const customer = useSelector(() => customerList.getCustomer())

    const isSaving = useSelector(() => molliePaymentList.getIsSavingObject())

    const amountOwingIncludingVatEuros = () => {
        return get(topup, "amount_including_vat_euros", 0)
    }

    const onPayByMollie = () => {
        const on_ok = function(mollie_record) {
            if ( mollie_record.checkout_url ) {
                setNavigatingToMollie(true)
                molliePaymentList.rememberPendingMolliePaymentId(mollie_record.id)
                window.location = mollie_record.checkout_url
            }

            if ( onClose ) {
                onClose({method: 'mollie'})
            }
        }

        setPayByMollieLoading(true)
        dispatch(molliePaymentList.startBuyTopup(topup)).then((res) => handleSubmitResult({res, on_ok}))
        
    }

    const onConfirmPayByBankTransfer = () => {
        const on_ok = function() {
            setInPayByBankTransfer(false)
            onClose({method: 'offline'})
        }

        setInPayByBankTransfer(true)
        dispatch(topupList.payOffline({topup: topup}))
            .then((res) => handleSubmitResult({res, on_ok: () => on_ok()}))
    }

    const onCancel = () => {
        onClose({method: 'cancel'})
    }

    const renderPayByBankTransfer = () => {
        return (
            <>
              <Modal.Body>
                <WrappingBusyMask isLoading={inPayByBankTransfer}>
                  <div>

                    <Trans
                        i18nKey="paytopupsmethodchoicepopup__pay_using_the_information_below"
                        defaults="Pay using the information below. Please make sure to use the correct payment reference."
                    />
                    
                    <br/><br/>
                    <Trans>Once your payment is received, your XOIP credits will be updated and the invoice will be made available.</Trans>
                    <br/>
                  </div>
                  <Separator variant="h20" />

                  <Col>
                    <Trans>Total (inc VAT)</Trans>:
                      &nbsp;&nbsp;
                    <CurrencyValue use_span={true} value={amountOwingIncludingVatEuros()} />
                  </Col>
                  <Separator variant="h10" />
                  <BankTransferPaymentDetails customer={customer} payment_ref={`${customer.credits_payment_ref}-${topup.amount_excluding_vat_euros}`}/>
                  
                </WrappingBusyMask>  
              </Modal.Body>

              <Modal.Footer>
                <ButtonBar>
                  <Button variant="danger" onClick={() => setPayByBankTransfer(false)}>
                    <Trans>Back</Trans>
                  </Button>
                  <Separator variant="w5" />
                  <Button variant="success" onClick={onConfirmPayByBankTransfer}>
                    <Trans>Confirm</Trans>
                  </Button>
                </ButtonBar>
              </Modal.Footer>
            </>
        )
    }
    
    const renderChoosePaymentMethod = () => {
        return (
            <Modal.Body>
              <p>
                <Trans>Choose a payment method</Trans>
              </p>

              <p>
                <BlueButton onClick={onPayByMollie}>
                  <Trans>Online payment</Trans>
                </BlueButton>
              </p>

              { topup.can_pay_by_bank_transfer &&
               <p>
                 <BlueButton onClick={() => setPayByBankTransfer(true)}>
                   <Trans>Pay by bank transfer</Trans>
                 </BlueButton>
               </p>
              }

              { ! topup.can_pay_by_bank_transfer &&
               <p>
                 <GrayButton disabled={true} onClick={{}}>
                   <Trans>Pay by bank transfer</Trans>
                 </GrayButton>
                 <ToolTip>
                    <Trans i18nKey={`paytopup__cannot_pay_by_bank_transfer__${topup.cannot_pay_by_bank_transfer_reason}`} />
                  </ToolTip>
               </p>
              }
            
              <p>
                <RedButton onClick={onCancel}>
                  <Trans>Cancel</Trans>
                </RedButton>
              </p>
            </Modal.Body>
        )
    }

    const renderPayByMollieLoading = () => {
        return (
            <>
              <Separator variant="h40" />
            </>
        )
    }

    return (
        <Modal show={true}
               size="lg"
               onHide={onCancel}
        >

          <Modal.Header closeButton>
            <Modal.Title>
              <Trans>Pay topup</Trans>
            </Modal.Title>
          </Modal.Header>

          <WrappingBusyMask isLoading={isSaving || navigatingToMollie || inPayByBankTransfer}>

            <LoadingOverlay
                active={isSaving}
                spinner
                text={<Trans>Please wait a moment...</Trans>}
            >
              <>

                { !payByMollieLoading && !payByBankTransfer && renderChoosePaymentMethod() }
                
                { payByMollieLoading &&
                  <>
                    { renderPayByMollieLoading() }
                  </>
                }

                { payByBankTransfer &&
                  <>
                    { renderPayByBankTransfer() }
                  </>
                }
              </>
              <Separator variant="h20" />
            </LoadingOverlay>
          </WrappingBusyMask>
          
        </Modal>
    )
    
}

const button_with_tooltip = css`
display: flex;
align-items: center;
`
